import { Minus, Plus, Trash2 } from 'lucide-react'

import { BackButton, PageHeader } from '@/components/atoms'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useBusiness } from '@/contexts/BusinessContext'
import { useCreateOrder } from '@/hooks/features/orders'
import { CustomerSearch } from '@/components/features/orders/CustomerSearch'

export const CreateOrder = () => {
  const {
    loading,
    selectedCustomer,
    setSelectedCustomer,
    customerInfo,
    setCustomerInfo,
    deliveryInfo,
    setDeliveryInfo,
    items,
    addItem,
    removeItem,
    updateQuantity,
    paymentMethod,
    setPaymentMethod,
    discount,
    setDiscount,
    deliveryLocationTypeId,
    setDeliveryLocationTypeId,
    handleCreate,
    navigate,
  } = useCreateOrder()



  const { formatCurrency } = useBusiness()

  const subtotal = items.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const total = subtotal - discount

  return (
    <div className="mx-auto w-full max-w-[1088px] pb-10">
      <PageHeader
        size="large"
        title="Create order"
        back={<BackButton onClick={() => navigate('/orders')} label="Back to orders" />}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate('/orders')}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? 'Creating…' : 'Create order'}
            </Button>
          </>
        }
      />

      <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_288px] lg:gap-6">
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Products</CardTitle>
            </CardHeader>
            <CardContent className="space-y-1.5">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Label htmlFor="newItemId" className="text-sm text-ink">Product variation ID</Label>
                  <Input placeholder="Enter a product variation ID" id="newItemId" />
                </div>
                <div className="flex items-end gap-2">
                  <div className="w-20 space-y-1.5">
                    <Label htmlFor="newItemQty" className="text-sm text-ink">Quantity</Label>
                    <Input type="number" defaultValue={1} id="newItemQty" />
                  </div>
                  <Button
                    variant="secondary"
                    size="lg"
                    onClick={() => {
                      const idInput = document.getElementById('newItemId') as HTMLInputElement
                      const qtyInput = document.getElementById('newItemQty') as HTMLInputElement
                      if (idInput.value) {
                        addItem({
                          product_variation_id: idInput.value,
                          quantity: Number(qtyInput.value) || 1,
                          name: `Item ${idInput.value.slice(-4)}`,
                          price: 0 // Price is not known yet
                        })
                        idInput.value = ''
                        qtyInput.value = '1'
                      }
                    }}
                  >
                    <Plus />
                    Add item
                  </Button>
                </div>
              </div>
            </CardContent>
            <div className="border-t border-border">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Product</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="w-12">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow className="hover:bg-transparent">
                      <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                        No items added yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item) => (
                      <TableRow key={item.product_variation_id}>
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">{formatCurrency(item.price)}</TableCell>
                        <TableCell>
                          <div className="flex w-fit items-center gap-1 rounded-md border border-border bg-card p-0.5">
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Decrease quantity"
                              onClick={() => updateQuantity(item.product_variation_id, item.quantity - 1)}
                            >
                              <Minus />
                            </Button>
                            <span className="w-6 text-center tabular-nums">{item.quantity}</span>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              aria-label="Increase quantity"
                              onClick={() => updateQuantity(item.product_variation_id, item.quantity + 1)}
                            >
                              <Plus />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {formatCurrency(item.price * item.quantity)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label="Remove item"
                            className="text-muted-foreground hover:text-error"
                            onClick={() => removeItem(item.product_variation_id)}
                          >
                            <Trash2 />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex items-center justify-between gap-2">
                <span className="text-text-2">Subtotal</span>
                <span className="tabular-nums text-ink">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="order-discount" className="text-sm font-normal text-text-2">Discount</Label>
                <Input
                  id="order-discount"
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(Number(e.target.value))}
                  className="w-28 text-right tabular-nums"
                />
              </div>
              <div className="flex items-center justify-between gap-2 border-t border-border-subtle pt-2">
                <span className="font-semibold text-ink">Total</span>
                <span className="font-semibold tabular-nums text-ink">{formatCurrency(total)}</span>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Customer</CardTitle>
            </CardHeader>
            <CardContent>
              <CustomerSearch
                customerInfo={customerInfo}
                setCustomerInfo={setCustomerInfo}
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Delivery address</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="delivery-address" className="text-sm text-ink">Address</Label>
                <Input
                  id="delivery-address"
                  value={deliveryInfo.address}
                  onChange={(e) => setDeliveryInfo({ ...deliveryInfo, address: e.target.value, name: customerInfo.name, phone: customerInfo.phone })}
                  placeholder="Full delivery address"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="delivery-location-type" className="text-sm text-ink">Location type ID</Label>
                <Input
                  id="delivery-location-type"
                  value={deliveryLocationTypeId}
                  onChange={(e) => setDeliveryLocationTypeId(e.target.value)}
                  placeholder="Location type ID"
                />
                <p className="text-sm text-text-2">Required.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Payment method</CardTitle>
            </CardHeader>
            <CardContent>
              <Select value={paymentMethod} onValueChange={(val: any) => setPaymentMethod(val)}>
                <SelectTrigger aria-label="Payment method">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cod">Cash on delivery</SelectItem>
                  <SelectItem value="online">Online payment</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
